// hooks/auth/useForgotPassword.ts
import { useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { useAppDispatch, useAppSelector } from '@/store';
import { selectAuthStatus, selectAuthError } from '@/store/selectors/auth';
import { forgotPasswordThunk } from '@/store/thunks/auth/forgotPasswordThunk';
import { clearLoginError } from '@/store/slices/auth/authSlice';

/**
 * Hook for /forgot-password route
 * Handles email submission to request a password reset link
 *
 * @returns Hook state and handlers
 */
export function useForgotPassword() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const authStatus = useAppSelector(selectAuthStatus);
  const error = useAppSelector(selectAuthError);
  const isLoading = authStatus === 'loading';

  useEffect(() => {
    dispatch(clearLoginError());
  }, [dispatch]);

  const submit = async(values: { emailAddress: string }) => {
    const action = await dispatch(forgotPasswordThunk(values));
    if (forgotPasswordThunk.fulfilled.match(action)) {
      await navigate({ to: '/reset-password' });
    }
  };

  return {
    state: { isLoading, error },
    handlers: { submit },
  };
}
